import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';

import useFilterDropdownOptions, {FilterDropdownOptionsProps} from './useFilterDropdownOptions';

export interface ArchivableOptionData extends OptionData {
  isArchived?: boolean;
}

function isArchivableOptionData(optionData: OptionData): optionData is ArchivableOptionData {
  return (optionData as ArchivableOptionData).isArchived !== undefined;
}

/**
 * React hook that can be used to turn a regular dropdown component into one that filters out archived options
 * by default, with a button to make them visible.
 *
 * Expected usage:
 * - Spread the object returned by this hook into the props being passed to a dropdown component
 * - You may need to map the properties to different prop names if the dropdown component in question expects
 * the same conceptual props, but with different names
 *
 * @param optionDatas - the options for the dropdown component that's using this hook
 * @param showMoreText - the text to display on the button that shows the hidden options
 * @param onChangeValueBase - the onChangeValue fn defined in the dropdown component that's using this hook
 * @param renderOptionContentBase - optional. If the component using this hook has a custom implementation of
 *  renderOptionContent, provide it here.
 */
export default function useFilterArchivedDropdownOptions(
  optionDatas: Array<ArchivableOptionData>,
  showMoreText: string,
  onChangeValueBase: (value: OptionValue) => void,
  renderOptionContentBase?: (optionData: OptionData, shouldFade: boolean) => JSX.Element
): FilterDropdownOptionsProps {
  const filterFunc = (optionData: OptionData): boolean =>
    !isArchivableOptionData(optionData) || !optionData.isArchived;
  return useFilterDropdownOptions(
    optionDatas,
    filterFunc,
    showMoreText,
    onChangeValueBase,
    renderOptionContentBase
  );
}
